import React from "react";
import PatientEnCoursLine from "./PatientEnCoursLine.tsx";
import styles from "./PatientsStateManager.module.css";
import SimpleWindow from "../../../components/SimpleWindow/SimpleWindow.tsx";
import PatientStateCommentEdit from "./PatientStates/PatientStateCommentEdit.tsx";
import { match } from "ts-pattern";
import type { PatientEnCours } from "cadendar-shared";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import {
  defaultActivePatientSort,
  sortActivePatients,
  type ActivePatientSortColumn,
} from "./sortActivePatients.ts";

interface CommentToShowState {
  type: "show";
  comment: string;
  x: number;
  y: number;
}

interface CommentToEditState {
  type: "edit";
  comment: string;
  x: number;
  y: number;
  patientId: string;
  stateId: number | null;
}

type CommentState = CommentToShowState | CommentToEditState | { type: "none" };

interface ShowCommentAction {
  type: "show";
  comment: string;
  x: number;
  y: number;
}

interface HideCommentAction {
  type: "hide";
}

interface ShowCommentEditorAction {
  type: "showEditor";
  comment: string;
  x: number;
  y: number;
  patientId: string;
  stateId: number | null;
}

interface ResetCommentAction {
  type: "reset";
}

type CommentAction =
  | ShowCommentAction
  | HideCommentAction
  | ResetCommentAction
  | ShowCommentEditorAction;

function commentToShowReducer(
  state: CommentState,
  action: CommentAction,
): CommentState {
  return match(action)
    .with(
      {
        type: "show",
      },
      (action) => {
        if (state.type === "edit") {
          return state;
        }
        return {
          type: "show",
          comment: action.comment,
          x: action.x,
          y: action.y,
        } as const;
      },
    )
    .with({ type: "hide" }, () => {
      if (state.type === "show") {
        return { type: "none" } as const;
      }
      return state;
    })
    .with({ type: "showEditor" }, (action) => {
      return {
        type: "edit",
        comment: action.comment,
        x: action.x,
        y: action.y,
        patientId: action.patientId,
        stateId: action.stateId,
      } as const;
    })
    .with({ type: "reset" }, () => {
      return { type: "none" } as const;
    })
    .exhaustive();
}

interface PatientsStateManagerProps {
  patients: PatientEnCours[];
  sortType: "active" | "today";
}

interface PatientSortHeader {
  column: ActivePatientSortColumn;
  label: string;
  className: string;
}

const patientSortHeaders: PatientSortHeader[] = [
  { column: "name", label: "Patient", className: "col-span-2" },
  { column: "state", label: "État", className: "" },
  { column: "updatedAt", label: "Depuis", className: "" },
  { column: "quoteState", label: "Devis", className: "" },
  { column: "pecState", label: "PEC", className: "" },
  { column: "nextRdv", label: "Rdv programmé", className: "col-span-2" },
  { column: "comment", label: "Commentaire", className: "col-span-3" },
];

const hasTodoState = (patient: PatientEnCours) =>
  patient.pec_state === "todo" || patient.quote_state === "todo";

const hasWaitingForAnswerState = (patient: PatientEnCours) =>
  patient.pec_state === "waiting_for_answer";

const hasMustCallBackState = (patient: PatientEnCours) =>
  patient.state === "must_call_back";

const hasToExamineState = (patient: PatientEnCours) =>
  patient.state === "apt_booked" && !patient.next_rdv;

const PatientsStateManager = (props: PatientsStateManagerProps) => {
  const [commentState, dispatchCommentState] = React.useReducer(
    commentToShowReducer,
    { type: "none" },
  );
  const [activePatientSort, setActivePatientSort] = React.useState(
    defaultActivePatientSort,
  );
  const commentToShow =
    commentState.type === "show" ? commentState.comment : null;

  const commentPosition =
    commentState.type !== "none"
      ? { x: commentState.x, y: commentState.y }
      : { x: 0, y: 0 };
  const isEditingComment = commentState.type === "edit";
  const setCommentToShow = (comment: string, x: number, y: number) => {
    dispatchCommentState({
      type: "show",
      comment,
      x,
      y,
    });
  };
  const setCommentToHide = () => {
    dispatchCommentState({
      type: "hide",
    });
  };
  const setIsEditingComment = (
    comment: string,
    x: number,
    y: number,
    patientId: string,
    stateId: number | null,
  ) => {
    dispatchCommentState({
      type: "showEditor",
      comment,
      x,
      y,
      patientId,
      stateId,
    });
  };
  const resetEditingComment = () => {
    dispatchCommentState({
      type: "reset",
    });
  };
  const handleSortChange = (column: ActivePatientSortColumn) => {
    setActivePatientSort((currentSort) => ({
      column,
      direction:
        currentSort.column === column && currentSort.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const renderPatientLine = (
    patient: PatientEnCours,
    rowBackgroundClassName?: string,
  ) => (
    <PatientEnCoursLine
      nom={patient.name}
      prenom={patient.forename}
      state={patient.state}
      quoteState={patient.quote_state}
      pecState={patient.pec_state}
      patientId={patient.id}
      stateId={patient.stateId}
      key={patient.id}
      comment={patient.comment}
      updated_at={patient.updated_at}
      next_rdv={patient.next_rdv}
      rowBackgroundClassName={rowBackgroundClassName}
      onPeekComment={({ x, y }: { x: number; y: number }) => {
        setCommentToShow(patient.comment, x, y);
      }}
      onHideComment={setCommentToHide}
      onEditComment={({ x, y }: { x: number; y: number }) => {
        const windowWidth = document.documentElement.clientWidth;
        const windowHeight = document.documentElement.clientHeight;
        if (x + 400 > windowWidth) {
          x = windowWidth - 450;
        }
        if (y + 300 > windowHeight) {
          y = windowHeight - 350;
        }
        setIsEditingComment(patient.comment, x, y, patient.id, patient.stateId);
      }}
    />
  );

  let patientsSorted = props.patients;
  let activeSections: {
    title: string;
    patients: PatientEnCours[];
    rowBackgroundClassName?: string;
  }[] = [];

  if (props.sortType === "active") {
    const todoPatients = sortActivePatients(
      props.patients.filter(hasTodoState),
      activePatientSort,
    );
    const waitingForAnswerPatients = sortActivePatients(
      props.patients.filter(
        (patient) =>
          !hasTodoState(patient) && hasWaitingForAnswerState(patient),
      ),
      activePatientSort,
    );
    const mustCallBackPatients = sortActivePatients(
      props.patients.filter(
        (patient) =>
          !hasTodoState(patient) &&
          !hasWaitingForAnswerState(patient) &&
          hasMustCallBackState(patient),
      ),
      activePatientSort,
    );
    const toExaminePatients = sortActivePatients(
      props.patients.filter(
        (patient) =>
          !hasTodoState(patient) &&
          !hasWaitingForAnswerState(patient) &&
          !hasMustCallBackState(patient) &&
          hasToExamineState(patient),
      ),
      activePatientSort,
    );
    const otherPatients = sortActivePatients(
      props.patients.filter(
        (patient) =>
          !hasTodoState(patient) &&
          !hasWaitingForAnswerState(patient) &&
          !hasMustCallBackState(patient) &&
          !hasToExamineState(patient),
      ),
      activePatientSort,
    );

    activeSections = [
      {
        title: "PECs à faire",
        patients: todoPatients,
      },
      {
        title: "PECs en attente de réponse de la mutuelle",
        patients: waitingForAnswerPatients,
      },
      {
        title: "Doit rappeler",
        patients: mustCallBackPatients,
      },
      {
        title: "À examiner",
        patients: toExaminePatients,
        rowBackgroundClassName: "*:bg-amber-100",
      },
    ].filter((section) => section.patients.length > 0);

    patientsSorted = otherPatients;
  }
  if (props.sortType === "today") {
    //we do nothing for now because the patients are already ordered by today rdv time by the parent component
  }

  return (
    <div className={styles.centrer}>
      <div className={styles.container}>
        {props.sortType === "active" &&
          patientSortHeaders.map((header) => {
            const isActive = activePatientSort.column === header.column;
            const directionLabel =
              activePatientSort.direction === "asc"
                ? "croissant"
                : "décroissant";

            return (
              <button
                type="button"
                key={header.column}
                className={`${styles.sortButton} ${header.className}`}
                onClick={() => handleSortChange(header.column)}
                aria-pressed={isActive}
                aria-label={`${header.label}, ${
                  isActive ? `tri ${directionLabel}` : "tri disponible"
                }`}
              >
                {header.label}
                {isActive && (
                  <span
                    className={styles.sortDirection}
                    aria-hidden="true"
                    title={directionLabel}
                  >
                    {activePatientSort.direction === "asc" ? (
                      <FaArrowUp />
                    ) : (
                      <FaArrowDown />
                    )}
                  </span>
                )}
              </button>
            );
          })}
        {activeSections.map((section) => (
          <React.Fragment key={section.title}>
            <div className={styles.sectionTitle}>{section.title}</div>
            {section.patients.map((patient) =>
              renderPatientLine(patient, section.rowBackgroundClassName),
            )}
          </React.Fragment>
        ))}
        {patientsSorted.map((patient) => renderPatientLine(patient))}
      </div>
      {commentToShow && (
        <SimpleWindow
          isVisible={commentToShow != null}
          title="Commentaires"
          onClose={resetEditingComment}
          initialX={commentPosition.x - 100}
          initialY={commentPosition.y + 50}
        >
          <div style={{ whiteSpace: "pre-wrap" }}>
            <div className="w-96">{commentToShow}</div>
          </div>
        </SimpleWindow>
      )}
      {isEditingComment && (
        <SimpleWindow
          isVisible={isEditingComment}
          title="Édition du commentaire"
          onClose={resetEditingComment}
          initialX={commentPosition.x}
          initialY={commentPosition.y}
        >
          <PatientStateCommentEdit
            comment={commentState.comment}
            stateId={commentState.stateId}
            patientId={commentState.patientId}
            onClose={() => {
              dispatchCommentState({
                type: "reset",
              });
            }}
          />
        </SimpleWindow>
      )}
    </div>
  );
};

export default PatientsStateManager;
